import { Lock, TriangleAlert } from "lucide-react";
import { ONE_MINUTE_MS, ONE_HOUR_MS } from "../lib/time";

export const OVERSTAY_HOURS = 48;

export const durationLabel = (since, now) => {
  const mins = Math.max(0, Math.floor((now - new Date(since).getTime()) / ONE_MINUTE_MS));
  const h = Math.floor(mins / 60);
  return h > 0 ? `${h}h ${mins % 60}m` : `${mins}m`;
};

export const isOverstay = (s, now) =>
  s.status === "occupied" && !s.assigned_to && s.since && now - new Date(s.since).getTime() > OVERSTAY_HOURS * ONE_HOUR_MS;

const styleFor = (s, over) => {
  if (over) return "border-[#FF9F0A] text-[#FF9F0A] bg-[#FF9F0A]/15";
  if (s.status === "occupied") return "border-[#FF3B30] text-[#FF3B30] bg-[#FF3B30]/10";
  if (s.status === "reserved") return "border-[#FFD60A] text-[#FFD60A] bg-[#FFD60A]/10";
  return "border-[#262626] text-white hover:bg-[#1F1F1F]";
};

const Slot = ({ s, selected, onSelect, now, idx }) => {
  const over = isOverstay(s, now);
  return (
    <button
      data-testid={`slot-${s.number}`}
      onClick={() => onSelect(s.number)}
      style={{ animationDelay: `${idx * 8}ms` }}
      className={`animate-in fade-in h-24 flex flex-col justify-between items-start p-3 border text-left transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#007AFF] ${styleFor(
        s,
        over
      )} ${selected === s.number ? "ring-2 ring-[#007AFF]" : ""}`}
    >
      <span className="w-full flex items-center justify-between">
        <span className="text-lg font-bold tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
          {String(s.number).padStart(2, "0")}
        </span>
        {over && <TriangleAlert className="w-3.5 h-3.5" data-testid={`overstay-flag-${s.number}`} />}
        {!over && s.assigned_to && <Lock className="w-3 h-3 opacity-70" />}
      </span>
      <span className="w-full">
        <span className="block text-[10px] uppercase tracking-wide opacity-80 truncate">
          {s.plate || s.driver || (s.status === "available" ? "Free" : s.status)}
        </span>
        {s.status === "reserved" && (
          <span className="block text-[10px] uppercase tracking-wide font-semibold opacity-90">Reserved</span>
        )}
        {s.since && !s.assigned_to && (
          <span
            className="block text-[10px] font-semibold tracking-tight opacity-90"
            data-testid={`duration-${s.number}`}
          >
            {durationLabel(s.since, now)}
          </span>
        )}
      </span>
    </button>
  );
};

const groupByZone = (spaces) => {
  const groups = [];
  spaces.forEach((s) => {
    const zone = s.zone || null;
    const last = groups[groups.length - 1];
    if (last && last.zone === zone) last.items.push(s);
    else groups.push({ zone, items: [s] });
  });
  return groups;
};

export const ParkingGrid = ({ spaces, selected, onSelect, now }) => {
  const groups = groupByZone(spaces);
  const hasZones = spaces.some((s) => s.zone);

  return (
    <div className="border border-[#262626] bg-[#0A0A0A] p-4 sm:p-6 flex flex-col gap-6" data-testid="parking-grid">
      {groups.map((g, gi) => (
        <div key={`${g.zone || "unzoned"}-${gi}`} className="flex flex-col gap-3">
          {hasZones && (
            <div
              className="flex items-center gap-3"
              data-testid={`zone-header-${g.zone ? g.zone.replace(/\s+/g, "-").toLowerCase() : "unzoned"}`}
            >
              <span className="label-tech whitespace-nowrap">{g.zone || "Unzoned"}</span>
              <span className="h-px flex-1 bg-[#262626]" />
              <span className="label-tech">{g.items.length}</span>
            </div>
          )}
          <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-8 xl:grid-cols-10 gap-2">
            {g.items.map((s, idx) => (
              <Slot key={s.number} s={s} idx={idx} selected={selected} onSelect={onSelect} now={now} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
